// Las columnas de la pestaña «todas las líneas»: las del padre y las del hijo.
//
// EL REPARTO. El padre lleva el nivel LÍNEA —lo que es constante pase lo que pase
// con las facturas— y el hijo lleva el nivel ENTREGA: qué factura, con qué albarán,
// en qué carga. Ninguna magnitud sale en los dos, que es la regla de esta pantalla.
//
// LOS RÓTULOS SE REUSAN, NO SE REESCRIBEN. Toda columna que ya existe en las otras
// dos pestañas entra por `deLinea()`, que envuelve su `render` para que opere sobre
// la cabecera del grupo. Así la etiqueta es literalmente la misma variable, no una
// copia que alguien tendrá que acordarse de cambiar dos veces — y hay un test que lo
// comprueba. Es la queja nº 2 del cliente convertida en mecanismo.
import type { ReactNode } from 'react'
import type { PedidoVentaLinea } from '@/types'
import type { LineaAgrupada } from '@/utils/lineasAgrupadas'
import type { ResumenAlbaranes } from '@/utils/albaranesLinea'
import { fmtDate, fmtEur, fmtN, fmtQty } from '../formato'
import { apagadoCls, tenueCls } from './estilos'
import {
  celdaImporte, columnasCola, columnasIdentidad,
  type ColumnaLinea, type ContextoCeldas,
} from './celdasLinea'

export interface ColumnaAgrupada {
  key: string
  etiqueta: string
  ancho: string
  alinear?: 'right'
  valorOrden?: (g: LineaAgrupada) => string | number | undefined
  /** Suma en el pie. Aquí SÍ se puede sumar `qtyLinea`, al revés que en el grupo
   *  facturado: una línea aparece exactamente una vez, así que no se cuenta dos
   *  veces. Es justo la condición que el test de semántica comprueba. */
  agg?: { key: string; value: (g: LineaAgrupada) => number }
  formatoTotal?: (valor: number) => string
  render: (g: LineaAgrupada) => ReactNode
}

export interface ContextoAgrupadas extends ContextoCeldas {
  albaranesDe: (l: PedidoVentaLinea) => ResumenAlbaranes | undefined
}

/** Adapta una columna de las otras pestañas para que lea la cabecera del grupo.
 *  Conserva `key` y `etiqueta` intactas: ése es el punto. */
function deLinea(c: ColumnaLinea): ColumnaAgrupada {
  return {
    key: c.key,
    etiqueta: c.etiqueta,
    ancho: c.ancho,
    alinear: c.alinear,
    valorOrden: c.valorOrden ? g => c.valorOrden!(g.cabecera) : undefined,
    render: g => c.render(g.cabecera),
  }
}

/** Un contador con su detalle en el tooltip. Devuelve `—` en el caso vacío, que
 *  aquí significa «todavía no», no «se desconoce». */
function contador(valores: string[], dark: boolean, vacio: string): ReactNode {
  if (valores.length === 0) return <span className={tenueCls(dark)} title={vacio}>—</span>
  return (
    <span className="whitespace-nowrap" title={valores.join('\n')}>
      <span className="font-mono text-[10px]">{valores[0]}</span>
      {valores.length > 1 && (
        <span className={`ml-1 text-[10px] font-semibold tabular-nums ${tenueCls(dark)}`}>
          +{valores.length - 1}
        </span>
      )}
    </span>
  )
}

/** LA FILA PADRE: el nivel línea. */
export function columnasPadre(ctx: ContextoAgrupadas): ColumnaAgrupada[] {
  const { t, dark } = ctx
  const identidad = columnasIdentidad(ctx).map(deLinea)
  const cola = columnasCola(ctx).map(deLinea)

  return [
    ...identidad,
    {
      key: 'qtyLinea',
      etiqueta: t('salesOrders.line.qtyOriginal'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: g => g.cabecera.qtyLinea,
      // Sumable AQUÍ y sólo aquí: la línea sale una vez.
      agg: { key: 'qtyLinea', value: g => g.cabecera.qtyLinea },
      formatoTotal: fmtQty,
      render: g => (
        <span title={t('salesOrders.line.qtyLineTooltip')}>{fmtN(g.cabecera.qtyLinea)}</span>
      ),
    },
    {
      key: 'qtyServidaLinea',
      etiqueta: t('salesOrders.line.qtyServed'),
      ancho: 'w-[96px]',
      alinear: 'right',
      valorOrden: g => g.filas.reduce((s, f) => s + f.qtyServida, 0),
      agg: { key: 'qtyServidaLinea', value: g => g.filas.reduce((s, f) => s + f.qtyServida, 0) },
      formatoTotal: fmtQty,
      render: g => {
        const servida = g.filas.reduce((s, f) => s + f.qtyServida, 0)
        return (
          <span className={dark ? 'text-emerald-400' : 'text-emerald-600'}>{fmtN(servida)}</span>
        )
      },
    },
    {
      key: 'pendienteFacturarLinea',
      etiqueta: t('salesOrders.line.qtyPendingBilling'),
      ancho: 'w-[110px]',
      alinear: 'right',
      valorOrden: g => g.cabecera.qtySinFacturar,
      // De nivel LÍNEA y aquí la línea es una fila, así que ahora SÍ suma. En el
      // grupo facturado no podía: allí se repite en cada factura.
      agg: { key: 'pendienteFacturarLinea', value: g => g.cabecera.qtySinFacturar },
      formatoTotal: fmtQty,
      render: g => <span className="font-semibold">{fmtQty(g.cabecera.qtySinFacturar)}</span>,
    },
    {
      key: 'facturas',
      etiqueta: t('salesOrders.invoice.invoice'),
      ancho: 'w-[140px]',
      valorOrden: g => g.facturas[0] ?? '',
      render: g => contador(g.facturas, dark, t('salesOrders.line.sinFacturarAun')),
    },
    {
      key: 'albaranes',
      etiqueta: t('salesOrders.line.packingSlip'),
      ancho: 'w-[140px]',
      valorOrden: g => g.albaranes[0] ?? '',
      render: g => contador(g.albaranes, dark, t('salesOrders.line.packingSlipEmpty')),
    },
    {
      key: 'cargasAlbaran',
      etiqueta: t('salesOrders.detalle.cargaCol'),
      ancho: 'w-[150px]',
      valorOrden: g => g.cargas[0] ?? '',
      render: g => contador(g.cargas, dark, t('salesOrders.line.sinCargaAsociada')),
    },
    {
      key: 'importeNetoLinea',
      etiqueta: t('salesOrders.line.netAmount'),
      ancho: 'w-[112px]',
      alinear: 'right',
      valorOrden: g => g.filas.reduce((s, f) => s + f.importeNeto, 0),
      agg: { key: 'importeNetoLinea', value: g => g.filas.reduce((s, f) => s + f.importeNeto, 0) },
      formatoTotal: fmtEur,
      render: g => {
        const neto = g.filas.reduce((s, f) => s + f.importeNeto, 0)
        return neto ? fmtEur(neto) : <span className={apagadoCls(dark)}>—</span>
      },
    },
    ...cola,
  ]
}

/** Lo que la carga aporta, más allá de su número. Sólo `avp` lo tiene relleno. */
export interface DatosCarga {
  /** `cargacliente_custom`. NO es un nombre de cliente: es el destino —`USA 14`,
   *  `CZECH REP`—, y por eso la columna se llama «Destino» y no «End customer». */
  destino?: string
  /** `referenciacliente_custom`: la referencia del cliente para esa carga. */
  referencia?: string
}

export interface ColumnaHija {
  key: string
  etiqueta: string
  ancho: string
  alinear?: 'right'
  render: (f: PedidoVentaLinea) => ReactNode
}

/** LA FILA HIJA: una por entrega —factura, albarán y carga—. */
export function columnasHijas(
  ctx: ContextoAgrupadas,
  datosCarga: (carga: string) => DatosCarga,
): ColumnaHija[] {
  const { t, dark } = ctx
  const vacio = () => <span className={tenueCls(dark)}>—</span>

  /** El destino / la referencia de TODAS las cargas de la fila, no de la primera.
   *
   *  Una factura puede cubrir dos envíos —medido: la factura AP-000001 del pedido
   *  26APPV000007 salió en las cargas 26APCG000010 y 26APCG000011—. Coger
   *  `cargas[0]` enseñaría el destino de una como si fuera el de la fila: un dato
   *  correcto en el sitio equivocado, que es peor que un hueco porque nadie lo
   *  cuestiona. */
  const refsDe = (f: PedidoVentaLinea, campo: 'destino' | 'referencia') => {
    const cargas = ctx.albaranesDe(f)?.cargas ?? []
    const valores = [...new Set(
      cargas.map(c => datosCarga(c)[campo]).filter(Boolean) as string[],
    )]
    return valores.join(' · ')
  }

  return [
    {
      key: 'factura',
      etiqueta: t('salesOrders.invoice.invoice'),
      ancho: 'w-[130px]',
      render: f => (f.factura?.trim()
        ? <span className="font-mono text-[10px] font-semibold">{f.factura.trim()}</span>
        : <span className={tenueCls(dark)} title={t('salesOrders.line.sinFacturarAun')}>
            {t('salesOrders.invoice.unbilled')}
          </span>),
    },
    {
      key: 'fechaFactura',
      etiqueta: t('salesOrders.detalle.fechaFactura'),
      ancho: 'w-[96px]',
      alinear: 'right',
      render: f => (f.primeraFechaFactura ? fmtDate(f.primeraFechaFactura) : vacio()),
    },
    {
      key: 'qtyPedida',
      etiqueta: t('salesOrders.line.qtyOrdered'),
      ancho: 'w-[96px]',
      alinear: 'right',
      render: f => <span title={t('salesOrders.line.qtyOrderedTooltip')}>{fmtN(f.qtyPedida)}</span>,
    },
    {
      key: 'qtyFacturada',
      etiqueta: t('salesOrders.invoice.billedQty'),
      ancho: 'w-[96px]',
      alinear: 'right',
      render: f => fmtQty(f.qtyFacturada ?? 0),
    },
    {
      key: 'albaranes',
      etiqueta: t('salesOrders.line.packingSlip'),
      ancho: 'w-[140px]',
      render: f => {
        const r = ctx.albaranesDe(f)
        if (!r || r.albaranes.length === 0) {
          return <span className={tenueCls(dark)} title={t('salesOrders.line.packingSlipEmpty')}>—</span>
        }
        return contador(r.albaranes, dark, '')
      },
    },
    {
      key: 'cargaAlbaran',
      etiqueta: t('salesOrders.detalle.cargaCol'),
      ancho: 'w-[150px]',
      render: f => {
        const cargas = ctx.albaranesDe(f)?.cargas ?? []
        if (cargas.length === 0) {
          // El hueco es un HECHO, no una laguna: el 98,8 % de estos casos son
          // entregas que no pasaron por una carga, y cinco empresas no gestionan
          // envíos con cargas en absoluto. De ahí el texto, que no promete que
          // vaya a llegar.
          return (
            <span className={tenueCls(dark)} title={t('salesOrders.line.sinCargaAsociadaTooltip')}>
              {t('salesOrders.line.sinCargaAsociada')}
            </span>
          )
        }
        return contador(cargas, dark, '')
      },
    },
    {
      key: 'destino',
      etiqueta: t('salesOrders.line.destino'),
      ancho: 'w-[130px]',
      render: f => {
        const v = refsDe(f, 'destino')
        return v ? <span title={t('salesOrders.line.destinoTooltip')}>{v}</span> : vacio()
      },
    },
    {
      key: 'refCliente',
      etiqueta: t('salesOrders.line.refCliente'),
      ancho: 'w-[120px]',
      render: f => {
        const v = refsDe(f, 'referencia')
        return v ? <span className="font-mono text-[10px]">{v}</span> : vacio()
      },
    },
    {
      key: 'importeFacturado',
      etiqueta: t('salesOrders.detalle.importeFacturado'),
      ancho: 'w-[120px]',
      alinear: 'right',
      render: f => celdaImporte(f.importeFacturado, f.moneda, dark),
    },
  ]
}
